import React, { useEffect, useState, useCallback } from 'react';
import { Content, InfoCard, Header } from '@backstage/core-components';
import {
  FormControl,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  CircularProgress,
  Box,
  makeStyles,
  FormGroup,
  Checkbox,
  FormHelperText,
  Divider,
} from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import enCA from 'date-fns/locale/en-CA';
import frCA from 'date-fns/locale/fr-CA';
import { useForm, Controller } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import {
  useApi,
  identityApiRef,
  configApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEpDevPNotifications } from '@solace-labs/backstage-plugin-ep-devp-react';
import { useNavigate } from 'react-router-dom';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';

const useStyles = makeStyles((theme) => ({
  content: {
    flex: "1 1 auto",
    height: "calc(100vh - 189px)",
    overflowY: "auto",
    
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '-ms-overflow-style': 'none',
    'scrollbar-width': 'none',
  },
  checkboxItemGrid: {
  width: '100%',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.875rem',
  },
},
  navigation: {
    backgroundColor: theme.palette.background.paper,
    height: '4rem',
    width: '100%',
    position: 'sticky',
    bottom: 0,
    zIndex: 100,
    marginTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  checkboxGroup: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  checkboxItemHorizontal: {
    marginRight: theme.spacing(2), // Space between checkboxes
    marginBottom: theme.spacing(1), // Small bottom margin for wrapping
    minWidth: 'auto', // Allow natural width
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      whiteSpace: 'nowrap', // Prevent label wrapping
    },
    [theme.breakpoints.down('md')]: {
      minWidth: '45%', // Two per row on medium screens
      marginRight: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%', // Stack on small screens
      marginRight: 0,
    },
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
  },
 checkboxItem: {
    marginBottom: theme.spacing(0.5),
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
    },
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
  requiredIndicator: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5),
  },
  formCard: {
    padding: theme.spacing(3),
  },
  sectionDivider: {
    margin: theme.spacing(2, 0),
  },
  submitButtonContainer: {
    position: 'relative',
    display: 'inline-block',
  },
}));

function Alert(props: any) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

interface NotificationFormData {
  notificationType: 'General' | 'Services';
  description: string;
  isScheduled: 'true' | 'false';
  startDate?: string;
  services?: string[];
  lifecycleEvents?: string[];
}

type UserRoleResponse = {
  userID: string;
  roleID: string;
  roleName: string;
};

type ServiceResponse = {
  organizationEntityId: { id: string; displayName: string };
  entityId: { id: string; displayName: string };
};

// Lifecycle event options matching the database requirements
const LIFECYCLE_EVENTS = [
  { value: 'new_version', labelKey: 'notifications.lifecycleNewVersion' },
  { value: 'lifecycle_stage_change', labelKey: 'notifications.lifecycleStageChange' },
  { value: 'documentation_update', labelKey: 'notifications.lifecycleDocumentation' },
  { value: 'announcements', labelKey: 'notifications.lifecycleAnnouncements' },
  { value: 'service_impact', labelKey: 'notifications.lifecycleServiceImpact' },
] as const;

interface NotificationFormProps {
  userRole: UserRoleResponse;
}

export const NotificationForm: React.FC<NotificationFormProps> = ({ 
  userRole: initialUserRole 
}) => {
  const { t, locale } = useServiceRegistryTranslation();
  const navigate = useNavigate();
  const classes = useStyles();
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);
  const createNotification = useEpDevPNotifications();

  // State management
  const [services, setServices] = useState<ServiceResponse[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedServiceIds, setSelectedServicesIds] = useState<string[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredServices, setFilteredServices] = useState<ServiceResponse[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<NotificationFormData>({
    defaultValues: {
      notificationType: 'General',
      description: '',
      isScheduled: 'false',
      startDate: '',
      services: [],
      lifecycleEvents: [],
    },
    mode: 'onChange', // Enable real-time validation
  });

  // Fetch services for the dropdown
  const fetchServices = useCallback(async () => {
    try {
      setLoadingServices(true);
      const { token } = await identityApi.getCredentials();

      const response = await fetch(
        `${baseUrl}/api/ep-devp-consumer-products/nav-canada`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.statusText}`);
      }

      const result = await response.json();
      const servicesList = result.list || [];
      setServices(servicesList);
      setFilteredServices(servicesList);
    } catch (error) {
      console.error('Error fetching services:', error);
      alertApi.post({
        message: t('notifications.servicesFailed'),
        severity: 'error',
        display: 'transient'
      });
    } finally {
      setLoadingServices(false);
    }
  }, [baseUrl, identityApi, alertApi]);

  const notificationType = watch('notificationType');
  const isScheduled = watch('isScheduled') === 'true';
  
  // Effects
  useEffect(() => {
    if (notificationType === 'Services' && services.length === 0 && !loadingServices) {
      fetchServices();
    }
  }, [notificationType, services.length, loadingServices, fetchServices]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredServices(services);
    } else {
      const filtered = services.filter(service =>
        service.entityId.displayName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      );
      setFilteredServices(filtered);
    }
  }, [searchTerm, services]);

  // Form submission
  const onSubmit = async (data: NotificationFormData) => {
    if (!initialUserRole) {
      alertApi.post({
        message: t('notifications.roleFailed'),
        severity: 'error',
        display: 'transient'
      });
      return;
    }

    setLoading(true);

    try {
      const payload: any = {
        notificationType: data.notificationType,
        description: data.description,
        userId: initialUserRole.userID,
        isScheduled: data.isScheduled,
        services: selectedServices,
        serviceIds: selectedServiceIds,
        lifecycleEvents: data.lifecycleEvents || [],
      };

      // Handle datetime formatting
      if (data.isScheduled === 'true' && data.startDate) {
        payload.startDatetime = formatDateTime(data.startDate);
      }
      
      const { token } = await identityApi.getCredentials();
      const response = await fetch(`${baseUrl}/api/notifications/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        createNotification(t('notifications.created'));
        
        // Reset form and state
        reset();
        setSelectedServices([]);
        setSelectedServicesIds([]);
        setSearchTerm('');
        
        // Navigate back to notifications list
        navigate('/notifications/view');
      } else {
        alertApi.post({
          message: t('notifications.createFailed'),
          severity: 'error',
          display: 'transient'
        });
      }
    } catch (error) {
      console.error('Error creating notification:', error);
      alertApi.post({
        message: t('notifications.createFailed'),
        severity: 'error',
        display: 'transient'
      });
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const formatDateTime = (date: string | Date): string => {
    const dateObj = new Date(date);
    return dateObj.toISOString().slice(0, 19).replace('T', ' ');
  };
  
  const handleCancel = () => {
    if (isDirty) {
      if (window.confirm(t('notifications.unsavedChanges'))) {
        navigate('/notifications/view');
      }
    } else {
      navigate('/notifications/view');
    }
  };

  return (
    <>
      <Header title={t('notifications.title')} subtitle={t('notifications.createSubtitle')} />
      <Box flexGrow={1} padding={3} className={classes.content}>
        <Content>
          <InfoCard className="notification-card">
            <Box className={classes.formCard}>
              <form
                id="notification-form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                {/* Notification Type */}
                <div className={classes.formSection}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    {t('notifications.type')}
                  </Typography>
                  <Controller
                    name="notificationType"
                    control={control}
                    render={({ field }) => (
                      <FormControl component="fieldset">
                        <RadioGroup {...field} row>
                          <FormControlLabel
                            value="General"
                            control={<Radio color="primary" />}
                            label={t('notifications.general')}
                          />
                          <FormControlLabel
                            value="Services"
                            control={<Radio color="primary" />}
                            label={t('notifications.selectServices')}
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </div>

                <Divider className={classes.sectionDivider} />

                {/* Lifecycle Events Multi-Select Checkbox Group */}
                <div className={classes.formSection}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    {t('notifications.lifecycleEvent')}
                    <span className={classes.requiredIndicator}>*</span>
                  </Typography>
                  <Controller
                    name="lifecycleEvents"
                    control={control}
                    rules={{
                      required: t('notifications.selectLifecycle'),
                      validate: value =>
                        value && value.length > 0 ? true : t('notifications.selectLifecycle'),
                    }}
                    render={({ field }) => (
                      <div>
                        <FormControl 
                          component="fieldset" 
                          error={!!errors.lifecycleEvents}
                          className={classes.checkboxGroup}
                        >
                          <Grid container spacing={1}>
                            {LIFECYCLE_EVENTS.map((event) => (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={event.value}>
                                <FormControlLabel
                                  className={classes.checkboxItemGrid}
                                  control={
                                    <Checkbox
                                      checked={field.value?.includes(event.value) || false}
                                      onChange={(e) => {
                                        const currentValues = field.value || [];
                                        if (e.target.checked) {
                                          field.onChange([...currentValues, event.value]);
                                        } else {
                                          field.onChange(
                                            currentValues.filter(val => val !== event.value)
                                          );
                                        }
                                      }}
                                      color="primary"
                                    />
                                  }
                                  label={t(event.labelKey)}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </FormControl>
                        {errors.lifecycleEvents && (
                          <Typography className={classes.errorText}>
                            {errors.lifecycleEvents.message}
                          </Typography>
                        )}
                      </div>
                    )}
                  />
                </div>

                {/* Services Dropdown */}
                {notificationType === 'Services' && (
                  <>
                    <Divider className={classes.sectionDivider} />
                    <div className={classes.formSection}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        {t('notifications.selectServices')}
                        <span className={classes.requiredIndicator}>*</span>
                      </Typography>
                      <Controller
                        name="services"
                        control={control}
                        rules={{
                          validate: value =>
                            notificationType === 'Services' &&
                            (!value || value.length === 0)
                              ? t('notifications.selectService')
                              : true,
                        }}
                        render={({ field }) => (
                          <Autocomplete
                            multiple
                            options={services}
                            getOptionLabel={option => option.entityId.displayName}
                            value={services.filter(service =>
                              field.value?.includes(service.entityId.displayName),
                            )}
                            onChange={(event, newValue) => {
                              const selectedServiceNames = newValue.map(
                                service => service.entityId.displayName,
                              );
                              const selectedServiceIds = newValue.map(
                                service => service.entityId.id,
                              );

                              field.onChange(selectedServiceNames);
                              setSelectedServicesIds(selectedServiceIds);
                              setSelectedServices(selectedServiceNames);
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label={t('notifications.services')}
                                placeholder={t('notifications.searchServices')}
                                error={!!errors.services}
                                helperText={errors.services?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingServices ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </div>
                  </>
                )}

                <Divider className={classes.sectionDivider} />

                {/* Description */}
                <div className={classes.formSection}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    {t('notifications.message')}
                    <span className={classes.requiredIndicator}>*</span>
                  </Typography>
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: t('notifications.messageRequired'),
                      minLength: {
                        value: 2,
                        message: t('notifications.messageMin'),
                      },
                      maxLength: {
                        value: 1000,
                        message: t('notifications.messageMax'),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        multiline
                        rows={4}
                        variant="outlined"
                        fullWidth
                        placeholder={t('notifications.messagePlaceholder')}
                        error={!!errors.description}
                        helperText={
                          errors.description 
                            ? errors.description.message 
                            : t('notifications.characterCount', { count: field.value?.length || 0 })
                        }
                      />
                    )}
                  />
                </div>

                <Divider className={classes.sectionDivider} />

                {/* Schedule */}
                <div className={classes.formSection}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    {t('notifications.scheduleQuestion')}
                  </Typography>
                  <Controller
                    name="isScheduled"
                    control={control}
                    render={({ field }) => (
                      <FormControl component="fieldset">
                        <RadioGroup {...field} row>
                          <FormControlLabel
                            value="false"
                            control={<Radio color="primary" />}
                            label={t('notifications.sendImmediately')}
                          />
                          <FormControlLabel
                            value="true"
                            control={<Radio color="primary" />}
                            label={t('notifications.scheduleLater')}
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </div>

                {/* Date Selection */}
                {isScheduled && (
                  <div className={classes.formSection}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      {t('notifications.scheduleDateTime')}
                      <span className={classes.requiredIndicator}>*</span>
                    </Typography>
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{
                        required: isScheduled ? t('notifications.scheduleRequired') : false,
                        validate: (value) => {
                          if (isScheduled && value) {
                            const selectedDate = new Date(value);
                            const now = new Date();
                            if (selectedDate <= now) {
                              return t('notifications.scheduleFuture');
                            }
                          }
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <MuiPickersUtilsProvider
                          utils={DateFnsUtils}
                          locale={locale === 'fr-CA' ? frCA : enCA}
                        >
                          <DateTimePicker
                            fullWidth
                            variant="inline"
                            inputVariant="outlined"
                            format="MM/dd/yyyy HH:mm"
                            ampm={false}
                            value={value ? new Date(value) : null}
                            onChange={(date) => {
                              onChange(date?.toISOString());
                            }}
                            minDate={new Date()}
                            error={!!errors.startDate}
                            helperText={errors.startDate ? errors.startDate.message : t('notifications.selectFuture')}
                            placeholder={t('notifications.selectDateTime')}
                          />
                        </MuiPickersUtilsProvider>
                      )}
                    />
                  </div>
                )}
              </form>
            </Box>
          </InfoCard>
        </Content>
      </Box>
      
      {/* Sticky action buttons at the bottom */}
      <div className={classes.navigation}>
        <Button
          color="secondary"
          variant="text"
          data-testid="cancel-button"
          onClick={handleCancel}
          disabled={loading}
        >
          {t('notifications.cancel')}
        </Button>
        
        <div className={classes.submitButtonContainer}>
          <Button
            color="primary"
            variant="contained"
            disabled={loading || !isValid}
            data-testid="submit-button"
            onClick={handleSubmit(onSubmit)}
            type="submit"
          >
            {loading ? t('notifications.creating') : t('notifications.create')}
          </Button>
          {loading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div>
      </div>
    </>
  );
};
